import { useState, useEffect, useCallback } from 'react';
import { Clock, Phone, Calendar, User, MapPin, RefreshCw, ChevronRight, Bell, AlertTriangle, Plus } from 'lucide-react';
import RappelDetailModal from './calendrier/RappelDetailModal';
import AddRappelModal from './AddRappelModal';
import getApiUrl from '../utils/getApiUrl';

const API = `${getApiUrl()}/api`;

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


/**
 * Widget affichant les rappels planifiés pour le télépro
 * Affiche les rappels du jour et de la semaine
 */
export default function MesRappelsWidget({ authUser, onAppelerLead }) {
  const [selectedRappel, setSelectedRappel] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [rappels, setRappels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('aujourdhui'); // 'aujourdhui' ou 'semaine'
  const [isExpanded, setIsExpanded] = useState(true);

  // Charger les rappels
  const loadRappels = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint = viewMode === 'aujourdhui' 
        ? `${API}/webhook/rappels/aujourdhui`
        : `${API}/webhook/rappels/semaine`;
      
      const url = authUser?.email 
        ? `${endpoint}?telepro_email=${encodeURIComponent(authUser.email)}`
        : endpoint;
      
      const resp = await authFetch(url);
      if (resp.ok) {
        const data = await resp.json();
        setRappels(data.rappels || []);
      }
    } catch (e) {
      console.error('Erreur chargement rappels:', e);
    }
    setLoading(false);
  }, [viewMode, authUser?.email]);

  useEffect(() => {
    loadRappels();
    // Rafraîchir toutes les 60 secondes
    const interval = setInterval(loadRappels, 60000);
    return () => clearInterval(interval);
  }, [loadRappels]);

  // Formater l'heure
  const formatHeure = (heure) => {
    if (!heure) return '';
    return heure;
  };

  // Formater la date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (dateStr === today.toISOString().split('T')[0]) return "Aujourd'hui";
    if (dateStr === tomorrow.toISOString().split('T')[0]) return "Demain";
    
    return date.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  // Vérifier si un rappel est en retard
  const isOverdue = (rappel) => {
    const now = new Date();
    const today = now.toISOString().split('T')[0];
    
    if (rappel.date_rappel < today) return true;
    if (rappel.date_rappel === today && rappel.heure_rappel) {
      const [h, m] = rappel.heure_rappel.split(':');
      const rappelTime = new Date(now);
      rappelTime.setHours(parseInt(h), parseInt(m), 0, 0);
      return now > rappelTime;
    }
    return false;
  };

  // Grouper les rappels par date
  const groupedRappels = rappels.reduce((acc, rappel) => {
    const date = rappel.date_rappel || 'Non définie';
    if (!acc[date]) acc[date] = [];
    acc[date].push(rappel);
    return acc;
  }, {});

  const totalRappels = rappels.length;
  const overdueCount = rappels.filter(isOverdue).length;

  return (
    <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
      {/* Header */}
      <div 
        className="px-4 py-3 bg-blue-500/10 border-b border-blue-500/20 flex items-center justify-between cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
            <Bell className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <h3 className="text-white font-semibold flex items-center gap-2">
              Mes rappels clients / leads
              {totalRappels > 0 && (
                <span className="bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {totalRappels}
                </span>
              )}
              {overdueCount > 0 && (
                <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  {overdueCount} en retard
                </span>
              )}
            </h3>
            <p className="text-sm text-slate-400">
              {viewMode === 'aujourdhui' ? "Rappels du jour" : "Rappels de la semaine"}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { 
              e.stopPropagation(); 
              setShowAddModal(true); 
            }}
            className="p-2 hover:bg-amber-500/30 bg-amber-500/20 rounded-lg transition-colors border border-amber-500/50"
            title="Ajouter un rappel"
            data-testid="add-rappel-btn"
          >
            <Plus className="w-4 h-4 text-amber-400" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); loadRappels(); }}
            className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
            title="Rafraîchir"
          >
            <RefreshCw className={`w-4 h-4 text-slate-400 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
        </div>
      </div>

      {isExpanded && (
        <>
          {/* Tabs */}
          <div className="flex border-b border-slate-700">
            <button
              onClick={() => setViewMode('aujourdhui')}
              className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                viewMode === 'aujourdhui' 
                  ? 'text-blue-400 border-b-2 border-blue-400 bg-blue-500/10' 
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              Aujourd'hui
            </button>
            <button
              onClick={() => setViewMode('semaine')}
              className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                viewMode === 'semaine' 
                  ? 'text-blue-400 border-b-2 border-blue-400 bg-blue-500/10' 
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              Cette semaine
            </button>
          </div>

          {/* Content */}
          <div className="p-4 max-h-80 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin text-blue-400" />
              </div>
            ) : rappels.length === 0 ? (
              <div className="text-center py-8">
                <Clock className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400">
                  {viewMode === 'aujourdhui' 
                    ? "Aucun rappel prévu aujourd'hui" 
                    : "Aucun rappel prévu cette semaine"}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {Object.entries(groupedRappels).map(([date, dateRappels]) => (
                  <div key={date}>
                    {/* Date header (uniquement en mode semaine) */}
                    {viewMode === 'semaine' && (
                      <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">
                        {formatDate(date)}
                      </div>
                    )}
                    
                    {/* Rappels de cette date */}
                    <div className="space-y-2">
                      {dateRappels.map((rappel, idx) => {
                        const overdue = isOverdue(rappel);
                        return (
                          <div 
                            key={rappel.lead_id || idx}
                            className={`p-3 rounded-lg border transition-colors cursor-pointer hover:border-blue-500/50 ${
                              overdue 
                                ? 'bg-red-500/10 border-red-500/30 hover:border-red-400/50' 
                                : 'bg-slate-800/50 border-slate-700'
                            }`}
                            onClick={() => setSelectedRappel(rappel)}
                            data-testid={`rappel-item-${rappel.lead_id || idx}`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                {/* Heure */}
                                <div className={`text-sm font-mono font-bold flex-shrink-0 ${overdue ? 'text-red-400' : 'text-blue-400'}`}>
                                  {formatHeure(rappel.heure_rappel) || '--:--'}
                                </div>
                                
                                {/* Infos lead */}
                                <div className="min-w-0 flex-1">
                                  <p className="text-white font-medium text-sm truncate">
                                    {rappel.prenom} {rappel.nom}
                                  </p>
                                  <div className="flex items-center gap-1 text-xs text-slate-400">
                                    <MapPin className="w-3 h-3 flex-shrink-0" />
                                    <span className="truncate">DPT {rappel.departement}</span>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Bouton téléphone - responsive avec taille adaptée */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.ringoverDial && window.ringoverDial(rappel.telephone);
                                }}
                                className={`flex-shrink-0 p-2 rounded-lg font-medium transition-colors ${
                                  overdue 
                                    ? 'bg-red-500 hover:bg-red-400 text-white' 
                                    : 'bg-blue-500/20 hover:bg-blue-500/30 text-blue-400'
                                }`}
                                title={`Appeler ${rappel.telephone}`}
                                data-testid={`rappel-call-btn-${rappel.lead_id || idx}`}
                              >
                                <Phone className="w-4 h-4" />
                              </button>
                            </div>
                            
                            {/* Notes si présentes */}
                            {rappel.notes && (
                              <p className="text-xs text-slate-400 mt-2 italic line-clamp-2">
                                "{rappel.notes}"
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
      
      {/* Modal détail rappel */}
      {selectedRappel && (
        <RappelDetailModal
          rappel={selectedRappel}
          onClose={() => setSelectedRappel(null)}
          onUpdate={() => {
            loadRappels();
            setSelectedRappel(null);
          }}
          onDelete={() => {
            loadRappels();
            setSelectedRappel(null);
          }}
        />
      )}
      
      {/* Modal ajouter rappel */}
      <AddRappelModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={() => {
          loadRappels();
          setShowAddModal(false);
        }}
        authUser={authUser}
      />
    </div>
  );
}
